import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { RefreshHistoryItemResponseApi } from "@/api/models/dataSourceApi";
import { getDataSourceRefreshHistory } from "@/api/services/dataSourceService";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useApp } from "@/contexts/AppContext";

interface SyncHistoryRecord {
  id: string;
  started: string;
  completed: string;
  triggerType: "Automatic" | "Manual";
  numberOfRecords: number;
  status: "Success" | "Fail";
  errorMessage?: string;
}

interface SyncHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataSourceName: string;
  dataSourceId: string;
}

// Demo mode mock data
const mockSyncHistory: SyncHistoryRecord[] = [
  {
    id: "1",
    started: "2024-01-15T14:30:00Z",
    completed: "2024-01-15T14:32:15Z",
    triggerType: "Manual",
    numberOfRecords: 1234,
    status: "Success",
  },
  {
    id: "2",
    started: "2024-01-15T10:00:00Z",
    completed: "2024-01-15T10:01:45Z",
    triggerType: "Automatic",
    numberOfRecords: 856,
    status: "Success",
  },
  {
    id: "3",
    started: "2024-01-14T10:00:00Z",
    completed: "2024-01-14T10:00:30Z",
    triggerType: "Automatic",
    numberOfRecords: 0,
    status: "Fail",
    errorMessage: "Connection timeout - unable to reach data source server",
  },
  {
    id: "4",
    started: "2024-01-13T10:00:00Z",
    completed: "2024-01-13T10:03:22Z",
    triggerType: "Automatic",
    numberOfRecords: 2567,
    status: "Success",
  },
  {
    id: "5",
    started: "2024-01-12T15:45:00Z",
    completed: "2024-01-12T15:46:10Z",
    triggerType: "Manual",
    numberOfRecords: 1890,
    status: "Success",
  },
];

/**
 * Maps an API refresh history item to the frontend SyncHistoryRecord format.
 */
const mapApiItemToRecord = (
  item: RefreshHistoryItemResponseApi,
): SyncHistoryRecord => {
  const statusLower = item.status.toLowerCase();
  const isSuccess = statusLower === "completed" || statusLower === "success";

  return {
    id: item.refreshId,
    started: item.startedAt ?? "",
    completed: item.completedAt ?? "",
    triggerType: item.triggerType === "Scheduled" ? "Automatic" : "Manual",
    numberOfRecords: item.recordsProcessed ?? 0,
    status: isSuccess ? "Success" : "Fail",
    errorMessage: item.errorMessage ?? undefined,
  };
};

const formatDateTime = (isoString: string): string => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const formatNumber = (num: number): string => {
  return num.toLocaleString("en-US");
};

const RECORD_LIMIT = 10;

/**
 * Sorts records by start date descending (most recent first).
 */
const sortByStartDateDesc = (
  records: SyncHistoryRecord[],
): SyncHistoryRecord[] => {
  return [...records].sort((a, b) => {
    if (!a.started && !b.started) return 0;
    if (!a.started) return 1;
    if (!b.started) return -1;
    return new Date(b.started).getTime() - new Date(a.started).getTime();
  });
};

export function SyncHistoryModal({
  isOpen,
  onClose,
  dataSourceName,
  dataSourceId,
}: SyncHistoryModalProps) {
  const { mode } = useApp();

  const [records, setRecords] = useState<SyncHistoryRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchHistory = useCallback(
    async (page: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getDataSourceRefreshHistory(
          Number(dataSourceId),
          page,
          RECORD_LIMIT,
        );
        const mapped = response.refreshes.map(mapApiItemToRecord);
        setRecords(sortByStartDateDesc(mapped));
        setCurrentPage(response.pagination.pageNumber);
        setTotalPages(response.pagination.totalPages);
        setTotalRecords(response.pagination.totalRecords);
      } catch (err) {
        console.error("Failed to fetch sync history:", err);
        setError("Failed to load sync history. Please try again.");
        setRecords([]);
      } finally {
        setIsLoading(false);
      }
    },
    [dataSourceId],
  );

  // Fetch data when modal opens in live mode
  useEffect(() => {
    if (isOpen && mode === "live") {
      setCurrentPage(1);
      fetchHistory(1);
    } else if (isOpen && mode === "demo") {
      setRecords(sortByStartDateDesc(mockSyncHistory));
      setTotalPages(1);
      setTotalRecords(mockSyncHistory.length);
    }
  }, [isOpen, mode, fetchHistory]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchHistory(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchHistory(currentPage + 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sync History - {dataSourceName}</DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
              <span className="text-muted-foreground">
                Loading sync history...
              </span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-600">{error}</span>
              <Button
                variant="outline"
                size="sm"
                className="ml-4"
                onClick={() => fetchHistory(currentPage)}
              >
                Retry
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Trigger Type</TableHead>
                  <TableHead className="text-right">
                    Number of Records
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Error Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-muted-foreground py-8"
                    >
                      No sync history available
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="text-sm">
                        {formatDateTime(record.started)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDateTime(record.completed)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            record.triggerType === "Automatic"
                              ? "secondary"
                              : "outline"
                          }
                        >
                          {record.triggerType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(record.numberOfRecords)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {record.status === "Success" ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="text-green-600 font-medium">
                                Success
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4 text-red-600" />
                              <span className="text-red-600 font-medium">
                                Fail
                              </span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {record.errorMessage && (
                          <span className="text-sm text-red-600">
                            {record.errorMessage}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          {mode === "live" && totalRecords > 0 && !isLoading && !error ? (
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalRecords} total
                records)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div />
          )}
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
